import { Component, HostListener, OnInit } from "@angular/core";
import { SimpleModalComponent, SimpleModalService } from "ngx-simple-modal";
import { MapService } from "../../services/map.service";
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import { Feature, Map, Overlay, View } from "ol";
import { transform } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Icon, Stroke, Style } from "ol/style";
import { LineString, Point } from "ol/geom";
import { Coordinates, RoutePointInformation, TooltipInformation } from "../../models/map/required-route-information";
import { take } from "rxjs/operators";
import { createEmpty, extend, Extent } from "ol/extent";
import { FullScreen, Zoom } from "ol/control";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { PbScreenService } from "../../../pb-screen/services/pb-screen.service";
import { ToScreenService } from "../../../pb-screen/services/to-screen.service";
import { SignalrListener } from "../../../pb-screen/helpers/signalr.listener";
import { NGXLogger } from "ngx-logger";
import { ToastrService } from "ngx-toastr";
import { Title } from "@angular/platform-browser";
import { SharedActions } from "../../shared.actions";
import { UsersAuthorizationService } from "../../../users-authorizations/user-authorization.service";
import { UserAuthorizationActions } from "../../../users-authorizations/user-authorization.actions";
import { NgRedux } from "@angular-redux/store";
import { ChannelService } from "../../../pb-screen/services/channel.service";


import { IAppState } from "../../../store/IAppState";
import { ChannelEventFactory } from "../../../pb-screen/services/channel.event.factory";

@Component({
  selector: "map-modal",
  templateUrl: "./map-modal.component.html",
  styleUrls: ["./map-modal.component.scss"]
})
export class MapModalComponent extends SignalrListener implements OnInit, MapModalInput {
  requiredRoutePointsInformation: RoutePointInformation[] = [];

  info = "";
  map!: Map;
  loadingMapDetails = true;
  private fullExtent: Extent;
  startIconColor = "#00FF00";
  stopIconColor = "cyan";

  private lineFeatures: Feature<LineString>[] = [];
  private iconFeatures: Feature<Point>[] = [];
  private highlightedLineFeatures = new Set();
  routeLineIndexClicked = -1;

  selectedRoutePoints: RoutePointInformation[] = [];

  showRouteDetailsTooltip = false;
  public selectedPlanningBlocksIds: number[];
  public selectedTransportOrdersIds: string[];
  orderLines: any[];

  @HostListener("document:keydown", ["$event"])
  onKeydownHandler(event: KeyboardEvent) {
    if (event.key === "Escape") {
      window.close();

    }
  }

  constructor(
    private mapService: MapService,
    private translate: TranslateService ,
    private route: ActivatedRoute,
    private pbservice: PbScreenService, 
    private toScreenService: ToScreenService ,
    public ngRedux: NgRedux<IAppState>,
            public channelService: ChannelService,
            public channelEventFactory: ChannelEventFactory,
            ngxLogger: NGXLogger,
            private readonly toastr: ToastrService,
            private readonly titleService: Title,
            sharedActions: SharedActions,
            private readonly usersService: UsersAuthorizationService,
            private readonly userAuthorizationActions: UserAuthorizationActions,
            private simpleModalService: SimpleModalService,
    
        ) {
    
            super(ngRedux, sharedActions, channelEventFactory, channelService, ngxLogger);
    
        }

  ngOnInit(): void {
    if (!this.selectedPlanningBlocksIds && !this.selectedTransportOrdersIds) { 
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        switch (params['os']) {
          case 'pb':
            this.selectedPlanningBlocksIds = params['ids']?.split(','); 
            break;
          case 'to':
            this.selectedTransportOrdersIds = params['ids']?.split(',');
            break;
          default:
            break;
        }
        this.fetchDataAndShowMap();
      })
    }
    else{
      this.ShowMap();
    }
  
  }

  fetchDataAndShowMap() {
    if (this.selectedPlanningBlocksIds?.length > 0) {
      this.pbservice.getPlanningBlocksByIds(this.selectedPlanningBlocksIds).pipe(take(1)).subscribe(planningBlocks => {
        if (planningBlocks.length == 0) {
          this.showError();
          return;
        }
        this.info = planningBlocks[0].info;
        this.orderLines = planningBlocks[0].planningOrderlines;
        
        this.ShowMap();
      });
    }
    else if(this.selectedTransportOrdersIds?.length > 0) {
      this.toScreenService.getTransportOrdersByIds(this.selectedTransportOrdersIds.join(",")).pipe(take(1)).subscribe(transportOrders => {
        if(transportOrders.length == 0) {
          this.showError();
          return;
        }
        this.info = transportOrders[0].info;
        this.orderLines = transportOrders[0].planningOrderlines;
        this.ShowMap();
      });
    }
  }
  showError() {
    throw new Error("Method not implemented.");
  }

  private ShowMap() {
    
      this.requiredRoutePointsInformation = [];

      for (const ol of this.orderLines) {
        this.requiredRoutePointsInformation.push({
          coordinates: {
            x: ol.longitude,
            y: ol.latitude
          },
          tooltipInformation: {
            title: `${ol.actionNumber} - ${ol.address.name}`,
            informationLines: [
              ol.address.name2,
              `${ol.address.street} ${ol.address.addressNumber}`,
              `${ol.address.countryCode}-${ol.address.postalCode} ${ol.address.city}`,
              ol.address.addressZoneName
            ]
          }
        });
      }

      this.mapService.fetchCoordinatesForEntireRoute(this.requiredRoutePointsInformation)
        .pipe(take(1))
        .subscribe((coordinatesList: Coordinates[][]) => {
          // CoordinatesList is a list of lists of coordinates
          // Each list of coordinates is a list of coordinates for a single route line
          // Each route line is a list of coordinates for a single route line
          // Each coordinate is a single point on the route line
          const startCoordinate = transform([coordinatesList[0][0].x, coordinatesList[0][0].y], "EPSG:4326", "EPSG:3857");

          this.map = new Map({
            layers: [new TileLayer({ source: new OSM() })],
            target: "map",
            view: new View({
              center: startCoordinate,
              zoom: 10,
              maxZoom: 20
            })
          });

          // Number to put on the 'stop icon' (1, 2, 3, etc.)
          let numberOnIcon = 2;

          for (let i = 0; i < coordinatesList.length; i++) {
            const routeLine = coordinatesList[i];

            // DRAW START ICON (ONLY IF THERE ARE MORE THAN 1 SET OF COORDINATES OTHERWISE END ICON IS USED)
            if (i === 0 && routeLine.length > 1) {
              const coordinate = routeLine[0];
              const startIconFeature = this.createPointFeatureWithIcon(
                coordinate,
                this.requiredRoutePointsInformation[0].tooltipInformation,
                () => this.createIconSvgDataURL(this.startIconColor, 1)
              );

              this.iconFeatures.push(startIconFeature);
            }

            // DRAW END ICON
            if (i === coordinatesList.length - 1) {
              const coordinate = routeLine[routeLine.length - 1];

              // requiredRouteInformation: Just need the last route point information
              const endIconFeature = this.createPointFeatureWithIcon(
                coordinate,
                this.requiredRoutePointsInformation[this.requiredRoutePointsInformation.length - 1].tooltipInformation,
                () => "/assets/img/map/end-pin-icon.svg"
              );

              this.iconFeatures.push(endIconFeature);
            } else {
              // DRAW STOP ICONS
              for (let j = 0; j < routeLine.length; j++) {
                if (j === routeLine.length - 1) {
                  const stopIconFeature = this.createPointFeatureWithIcon(
                    routeLine[j],
                    this.requiredRoutePointsInformation[i + 1].tooltipInformation,
                    () => this.createIconSvgDataURL(this.stopIconColor, numberOnIcon)
                  );

                  this.iconFeatures.push(stopIconFeature);

                  // Only increase the number if a number is used (number inside the icon)
                  numberOnIcon++;
                }
              }
            }

            // DRAWING THE ROUTE LINE (IF THERE ARE MORE THAN 1 SET OF COORDINATES)
            const lineFeature = this.createLineString(routeLine);
            this.lineFeatures.push(lineFeature);
          }


          // ADD ALL ROUTE LINE FEATURES TO MAP
          const vectorSourceDrivingRoute = new VectorSource({
            features: this.lineFeatures
          });

          const routeLineLayer = new VectorLayer({
            source: vectorSourceDrivingRoute,
            style: this.getDefaultRouteLineStyle()
          });

          this.map.addLayer(routeLineLayer);


          // ADD ALL ICON LAYERS TO MAP
          const vectorSourceIconFeatures = new VectorSource({
            features: this.iconFeatures
          });

          const pointIcons = new VectorLayer({ source: vectorSourceIconFeatures });

          this.map.addLayer(pointIcons);


          this.addTooltip();


          // FIT MAP TO CONTAIN ALL FEATURES
          const fullExtent = createEmpty();

          for (const layer of this.map.getLayers().getArray()) {
            if (layer instanceof VectorLayer) {
              const features = layer.getSource().getFeatures();

              for (const feature of features) {
                extend(fullExtent, feature.getGeometry().getExtent());
              }
            }
          }

          const height = fullExtent[3] - fullExtent[1];
          const additionalTopBuffer = height * 0.1; // 10% buffer at the top so the icon fits inside the map

          fullExtent[3] += additionalTopBuffer;

          // Used for centering the map on a click of the center button
          this.fullExtent = fullExtent;

          this.map.getView().fit(fullExtent, { padding: [30, 30, 30, 30], maxZoom: 20 });

          this.addFullScreenControlToMap();
          this.addZoomControlToMap();


          this.handleClickCenterButton();


          this.loadingMapDetails = false;
        });


      // Add All Route Points to start as selected
      this.selectedRoutePoints = this.requiredRoutePointsInformation;
  }


  private addTooltip() {
    const tooltip = document.getElementById("map-tooltip");

    const tooltipOverlay = new Overlay({
      element: tooltip,
      positioning: "bottom-center",
      stopEvent: false,
      offset: [25, -15] // Position the tooltip so the cursor is not directly over it
    });

    this.map.addOverlay(tooltipOverlay);

    this.map.on("pointermove", (evt) => {
      const feature = this.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);

      // Avoid showing tooltip on the line, only show it on the icons
      if (feature?.get("title")) {
        this.map.getTargetElement().style.cursor = "help";

        const title = feature.get("title");
        const description = feature.get("description");
        tooltip.innerHTML = `<strong>${ title }</strong><hr style="margin-top: 4px; margin-bottom: 8px;">${ description }`;

        tooltipOverlay.setPosition(evt.coordinate);
        tooltip.style.opacity = "1";
      } else {
        this.map.getTargetElement().style.cursor = "";
        tooltip.style.opacity = "0";
      }
    });
  }


  private createLineString(coordinates: Coordinates[]) {
    const mapSpecificCoordinates = coordinates.map(coord => transform([coord.x, coord.y], "EPSG:4326", "EPSG:3857"));

    const lineString = new LineString(mapSpecificCoordinates);

    return new Feature(lineString);
  }

  private createPointFeatureWithIcon(coordinate: Coordinates, tooltipInformation: TooltipInformation, createIcon: () => string) {
    const feature = new Feature({
      type: "icon",
      geometry: new Point(transform([coordinate.x, coordinate.y], "EPSG:4326", "EPSG:3857")),
      title: tooltipInformation.title,
      description: tooltipInformation.informationLines.filter(line => line?.trim().length > 0).join("<br>")
    });

    const startPinStyle = new Style({
      image: new Icon({
        src: createIcon(),
        scale: 1,
        anchor: [0.5, 1], // This makes the bottom of the icon point to the exact coordinate
        anchorXUnits: "fraction",
        anchorYUnits: "fraction"
      })
    });

    feature.setStyle(startPinStyle);

    return feature;
  }

  private createIconSvgDataURL(color: string, number: number) {
    let xPosition = 218;
    let radius = 60;
    let fontSize = 100;

    if (number > 9) {
      radius = 70;
      xPosition = 192;
      fontSize = 90;
    }

    const svgContent = `
    <svg width="64" height="64" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
        <path fill="${ color }" d="M336 96a80 80 0 1 0-96 78.39v283.17a32.09 32.09 0 0 0 2.49 12.38l10.07 24a3.92 3.92 0 0 0 6.88 0l10.07-24a32.09 32.09 0 0 0 2.49-12.38V174.39A80.13 80.13 0 0 0 336 96Zm-56 0a24 24 0 1 1 24-24a24 24 0 0 1-24 24Z" stroke="black" stroke-width="10"/>
        <circle cx="257" cy="95" r="${ radius }" fill="${ color }" />
        <text x="${ xPosition }" y="130" fill="black" font-family="Verdana" font-size="${ fontSize }" font-weight="bold">${ number }</text>
    </svg>`;

    return "data:image/svg+xml," + encodeURIComponent(svgContent);
  }

  highlightRoute(indexOfRouteLine: number) {
    if (this.loadingMapDetails) {
      return;
    }

    this.routeLineIndexClicked = indexOfRouteLine;

    // Set all icons to lower opacity
    for (const feature of this.iconFeatures) {
      const featureStyle = feature.getStyle();

      if (featureStyle instanceof Style) {
        const iconStyle = featureStyle.getImage();

        if (iconStyle instanceof Icon) {
          // Update the opacity
          iconStyle.setOpacity(0.3);

          // Reapply the style to the feature
          feature.setStyle(
            new Style({
              image: iconStyle,
            })
          );
        }
      }
    }

    // Set the selected route line icons to full opacity
    const startIconFeature = this.iconFeatures[indexOfRouteLine];
    const endIconFeature = this.iconFeatures[indexOfRouteLine + 1];

    for (const feature of [startIconFeature, endIconFeature]) {
      const featureStyle = feature.getStyle();

      if (featureStyle instanceof Style) {
        const iconStyle = featureStyle.getImage();

        if (iconStyle instanceof Icon) {
          iconStyle.setOpacity(1);

          feature.setStyle(
            new Style({
              image: iconStyle,
            })
          );
        }
      }
    }


    for (const feature of this.lineFeatures) {
      const indexOfClickedLineFeature = this.lineFeatures.indexOf(feature);

      if (indexOfClickedLineFeature === indexOfRouteLine) {
        feature.setStyle(this.getDefaultRouteLineStyle(100, 1, 5));

        if (this.highlightedLineFeatures.has(feature)) {
          // Stop highlighting
          this.highlightedLineFeatures.delete(feature);
        } else {
          // Start highlighting
          this.highlightedLineFeatures.add(feature);
        }
      } else {
        // Set not selected to lower opacity
        feature.setStyle(this.getDefaultRouteLineStyle(1, 0.3));

        this.highlightedLineFeatures.delete(feature);
      }
    }

    // Reset all line routes style
    if (this.highlightedLineFeatures.size === 0) {
      for (const feature of this.lineFeatures) {
        feature.setStyle(this.getDefaultRouteLineStyle());
      }

      // Reset all icons to full opacity
      for (const feature of this.iconFeatures) {
        const featureStyle = feature.getStyle();

        if (featureStyle instanceof Style) {
          const iconStyle = featureStyle.getImage();

          if (iconStyle instanceof Icon) {
            iconStyle.setOpacity(1);

            feature.setStyle(
              new Style({
                image: iconStyle,
              })
            );
          }
        }
      }

      this.routeLineIndexClicked = -1;

      this.selectedRoutePoints = this.requiredRoutePointsInformation;
    }
  }

  private getDefaultRouteLineStyle(zIndex = 1, opacity = 1, strokeWidth = 3) {
    return new Style({
      zIndex: zIndex,
      stroke: new Stroke({
        color: [0, 0, 255, opacity],
        width: strokeWidth
      })
    });
  }

  handleRouteDetailsTooltip(show: boolean) {
    this.showRouteDetailsTooltip = show;
  }

  private handleClickCenterButton() {
    document.querySelector("#center-button button").addEventListener("click", () => {
      this.map.getView().fit(this.fullExtent, { padding: [30, 30, 30, 30], maxZoom: 20 });
    });
  }

  private addFullScreenControlToMap() {
    this.translate.get(
      "MAP.FULLSCREEN_TOOLTIP"
    ).pipe(
      take(1)
    ).subscribe((translation) => {
      const control = new FullScreen({
        tipLabel: translation
      });

      this.map.addControl(control);
    });
  }

  private addZoomControlToMap() {
    this.translate.get([
      "MAP.ZOOM_IN_TOOLTIP",
      "MAP.ZOOM_OUT_TOOLTIP"
    ]).pipe(
      take(1)
    ).subscribe(translations => {
      // remove default zoom control
      const defaults = this.map.getControls().getArray();
      const zoomControl = defaults.find(element => {
        return element instanceof Zoom;
      });
      this.map.removeControl(zoomControl);


      const control = new Zoom({
        zoomInTipLabel: translations["MAP.ZOOM_IN_TOOLTIP"],
        zoomOutTipLabel: translations["MAP.ZOOM_OUT_TOOLTIP"]
      });

      this.map.addControl(control);
    });
  }
}

export interface MapModalInput {
   selectedPlanningBlocksIds: number[];
  selectedTransportOrdersIds: string[];
}
